import { getMuted, setMuted } from "./storage";

/**
 * Tiny SFX layer. Generates simple tones via WebAudio so we don't ship asset
 * files. iOS requires a user gesture to unlock the AudioContext — we lazily
 * create it on the first call.
 */

let ctx: AudioContext | null = null;
let unlocked = false;

function ensureCtx(): AudioContext | null {
  if (!ctx) {
    try {
      const Ctor =
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext ?? window.AudioContext;
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

export function unlockAudio(): void {
  const c = ensureCtx();
  if (!c) return;
  if (c.state === "suspended") {
    void c.resume();
  }
  unlocked = true;
}

function tone(freq: number, durationMs: number, type: OscillatorType = "sine", gain = 0.08): void {
  if (getMuted()) return;
  const c = ensureCtx();
  if (!c || !unlocked) return;
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, c.currentTime);
  g.gain.setValueAtTime(gain, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + durationMs / 1000);
  o.connect(g).connect(c.destination);
  o.start();
  o.stop(c.currentTime + durationMs / 1000);
}

export const sfx = {
  cardPlay: () => tone(660, 90, "triangle", 0.08),
  draw: () => tone(330, 110, "sine", 0.06),
  turnChime: () => tone(880, 140, "sine", 0.07),
  invalid: () => tone(180, 180, "sawtooth", 0.05),
  winnerFanfare: () => {
    tone(523, 120);
    setTimeout(() => tone(659, 120), 130);
    setTimeout(() => tone(784, 220), 270);
  },
};

export { getMuted, setMuted };
